import { Text } from "@clipboard-health/ui-components";
import { createTheme, Stack, ThemeProvider, useTheme } from "@mui/material";
import { Button } from "@src/appV2/redesign/components/Button";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import { HoldToConfirmButton } from "@src/appV2/redesign/components/HoldToConfirmButton";
import { TextButton } from "@src/appV2/redesign/components/TextButton";
import { ShieldIcon } from "@src/appV2/redesign/EmergencyCredits/components/ShieldIcon";
import { useMemo } from "react";

interface ShiftCancelFooterProps {
  variant: "preview-credit" | "cancel-shift";
  showWhyCantIUseCredit?: boolean;
  isCancelling?: boolean;
  isPreviewAnimating?: boolean;
  onKeepShift: () => void;
  onPressAndHoldToCancel: () => void;
  onPreviewCredit: () => void;
  onWhyCantIUseCredit?: () => void;
}

export function ShiftCancelFooter(props: ShiftCancelFooterProps): JSX.Element {
  const {
    variant,
    showWhyCantIUseCredit = false,
    isCancelling = false,
    isPreviewAnimating = false,
    onKeepShift,
    onPressAndHoldToCancel,
    onPreviewCredit,
    onWhyCantIUseCredit,
  } = props;

  const isCancelDisabled = isCancelling || isPreviewAnimating;

  const isPreviewCreditVariant = variant === "preview-credit";

  const baseTheme = useTheme();
  const destructiveHoldButtonTheme = useMemo(
    () =>
      createTheme(baseTheme, {
        palette: {
          components: {
            holdToConfirmButton: {
              default: baseTheme.palette.background.tertiary,
              confirmed: baseTheme.palette.intent?.error?.background,
            },
          },
        },
      }),
    [baseTheme]
  );

  return (
    <DialogFooter orientation="vertical" sx={{ marginTop: "auto" }}>
      <Stack spacing={3} sx={{ width: "100%" }}>
        {showWhyCantIUseCredit && (
          <TextButton
            underline
            sx={(theme) => ({
              color: theme.palette.primary.main,
              alignSelf: "center",
            })}
            onClick={onWhyCantIUseCredit}
          >
            Why can&apos;t I use my cancellation credit?
          </TextButton>
        )}
        {isPreviewCreditVariant ? (
          <Button
            fullWidth
            variant="contained"
            size="large"
            disabled={isPreviewAnimating}
            sx={(theme) => ({
              "&.Mui-disabled": {
                backgroundColor: theme.palette.intent?.disabled.background,
                border: `1px solid ${theme.palette.border?.primary}`,
              },
            })}
            onClick={onPreviewCredit}
          >
            <Stack direction="row" spacing={4} alignItems="center">
              <ShieldIcon variant="filled" sx={{ fontSize: 16 }} />
              <Text color="inherit">Preview my cancellation credit</Text>
            </Stack>
          </Button>
        ) : (
          <ThemeProvider theme={destructiveHoldButtonTheme}>
            <HoldToConfirmButton
              fullWidth
              size="large"
              disabled={isCancelDisabled}
              isLoading={isCancelling}
              sx={(theme) => ({
                color: theme.palette.error.light,
                border: `1px solid ${theme.palette.border?.primary}`,
              })}
              onConfirm={onPressAndHoldToCancel}
            >
              {isCancelling ? "Cancelling..." : "Press and hold to cancel"}
            </HoldToConfirmButton>
          </ThemeProvider>
        )}
        <Button
          fullWidth
          variant="outlined"
          size="large"
          disabled={isCancelling}
          sx={(theme) => ({
            color: theme.palette.text.primary,
            borderColor: theme.palette.border?.primary,
          })}
          onClick={onKeepShift}
        >
          Keep my shift
        </Button>
      </Stack>
    </DialogFooter>
  );
}
